import { useNavigate } from 'react-router-dom';
import { Calendar, Gift, CircleArrowRight } from 'lucide-react';
import { SubscriptionState } from '../../types';
import { requireAuth } from '../../utils/authUtils';
import { useTranslation } from 'react-i18next';
import OptimizedImageByName from '../common/OptimizedImageByName';
import { FEATURES } from '../../config';


// Data configuration for different service types
const HERO_CONFIG = {
  flower: {
    namespace: 'flowers',
    emoji: '🌸',
    heroImageName: 'flowerHero' as const,
    backgroundColor: 'bg-white',
    buttonStyle: 'bg-amber-400',
    buttonTextColor: 'text-white',
    buttonBorderRadius: 'rounded-full', // Rounded full for flower buttons
    imageScale: 'scale-100',
    imagePosition: 'object-center',
    customObjectPosition: undefined, // No custom position for flowers
    serviceBadge: null, // No badge for flowers
    pricing: {
      amount: 499,
      deliveries: 4,
      period: 'month'
    }
  },
  fruit: {
    namespace: 'fruits', 
    emoji: '🍎',
    heroImageName: 'fruitsHero' as const,
    backgroundColor: 'bg-white',
    buttonStyle: 'bg-orange-400',
    buttonTextColor: 'text-white',
    buttonBorderRadius: 'rounded-full', // Rounded full for fruit buttons
    imageScale: 'scale-100',
    imagePosition: '', // Empty since we'll use custom
    customObjectPosition: '50% 20%', // Custom position for fruits
    serviceBadge: {
      newLabel: 'NEW',
      serviceLabel: 'Service Release',
      bgColor: 'bg-white',
      textColor: 'text-orange-400',
      borderColor: 'border-orange-400',
      borderRadius: 'rounded-lg'
    },
    pricing: {
      amount: 699,
      deliveries: 4,
      period: 'month'
    }
  },
  cat_grass: {
    namespace: 'catGrass',
    emoji: '🌱',
    heroImageName: 'catGrass_hero' as const,
    backgroundColor: 'bg-white',
    buttonStyle: 'bg-green-500',
    buttonTextColor: 'text-white',
    buttonBorderRadius: 'rounded-full',
    imageScale: 'scale-100',
    imagePosition: 'object-center',
    customObjectPosition: undefined,
    serviceBadge: {
      newLabel: 'NEW',
      serviceLabel: 'Service Release',
      bgColor: 'bg-white',
      textColor: 'text-green-500',
      borderColor: 'border-green-500',
      borderRadius: 'rounded-lg'
    },
    pricing: {
      amount: 249,
      deliveries: 4,
      period: 'month'
    }
  }
} as const;

interface HeroSectionProps {
  isLoading: boolean;
  subscriptions: SubscriptionState[];
  handlePlanSelect: (plan: 'standard' | 'premium') => void;
  promoInfo?: string | null;
  serviceType?: 'flower' | 'fruit' | 'cat_grass';
}

export const HeroSection = ({ 
  isLoading, 
  subscriptions, 
  handlePlanSelect, 
  promoInfo,
  serviceType = 'flower' 
}: HeroSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get configuration for current service type
  const config = {
    ...HERO_CONFIG[serviceType],
    serviceBadge: HERO_CONFIG[serviceType].serviceBadge ? {
      ...HERO_CONFIG[serviceType].serviceBadge,
      newLabel: t('common.new'),
      serviceLabel: t(`${HERO_CONFIG[serviceType].namespace}.hero.serviceRelease`)
    } : null
  };
  
  // Add debug logging
  console.log('HeroSection Debug:', { 
    serviceType, 
    configAmount: config.pricing.amount,
    configDeliveries: config.pricing.deliveries,
    configPeriod: config.pricing.period
  });

  // Filter subscriptions by current service type and active status
  const currentServiceSubscriptions = subscriptions.filter(subscription => 
    subscription.serviceType === serviceType && 
    subscription.status === 'active'
  );

  const handleNavigate = (path: string) => {
    if (requireAuth(navigate, path)) {
      if (path === '/subscription') {
        navigate(path, {
          state: { 
            serviceType: serviceType,
            fromPage: serviceType === 'fruit' ? 'fruits' : serviceType === 'cat_grass' ? 'cat_grass' : 'home'
          }
        });
      } else if (path === '/gift-subscription') {
        navigate(path, {
          state: { 
            serviceType: serviceType,
            fromPage: serviceType === 'fruit' ? 'fruits' : serviceType === 'cat_grass' ? 'cat_grass' : 'home'
          }
        });
      } else {
        navigate(path);
      }
    }
  };

  return (
    <section 
      aria-label={`Introduction to Piika ${serviceType} subscription`}
      className={`relative flex flex-col justify-center items-center px-6 py-8 ${config.backgroundColor}`}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Removed yellow glowing decorative elements */}
      </div>
      
      <div className="relative max-w-md w-full text-center space-y-8">
        {/* Service Badge */}
        {config.serviceBadge && (
          <div className="flex justify-center">
            <div className={`inline-flex items-center gap-1 md:gap-1.5 ${config.serviceBadge.bgColor} ${config.serviceBadge.textColor} ${config.serviceBadge.borderRadius}`}>
              <span className={`text-sm md:text-md font-medium uppercase border-2 ${config.serviceBadge.borderColor} rounded-md px-1`}>
                {config.serviceBadge.newLabel}
              </span>
              <span className="text-base md:text-lg font-medium">
                {config.serviceBadge.serviceLabel}
              </span>
            </div>
          </div>
        )}

        {/* Title Section */}
        <div className="space-y-3">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight my-5">
            {t(`${config.namespace}.hero.title`)}
          </h1>
          
          <p className="text-base md:text-lg text-gray-600 leading-relaxed px-4">
            {t(`${config.namespace}.hero.description`)}
          </p>
        </div>
        
        {/* Main Action Button */}
        {isLoading ? (
          <div className="w-full py-6">
            <div className="w-8 h-8 border-t-2 border-r-2 border-amber-500 rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-700 mt-3">{t(`${config.namespace}.hero.loading`)}</p>
          </div>
        ) : currentServiceSubscriptions.length > 0 ? (
          <div className="space-y-3">
            <button
              onClick={() => handleNavigate('/subscription')}
              className={`w-full ${config.buttonStyle} ${config.buttonTextColor} ${config.buttonBorderRadius} px-8 py-6 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-between group`}
              disabled={isLoading}
              aria-label={`Manage your active ${serviceType} subscription`}
            >
              <span>{t(`${config.namespace}.hero.selectedPlan`)}</span>
              <CircleArrowRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform" />
            </button>
            
            <div className="space-y-2">
              <p className="text-gray-500 text-sm">{t(`${config.namespace}.hero.nextDelivery`)}</p>
            </div>
          </div>
        ) : !FEATURES.ENABLE_SUBSCRIPTION_PURCHASES ? (
          <div className="space-y-3">
            <div className="bg-gray-100 border border-gray-300 rounded-full px-8 py-4 text-center">
              <p className="text-gray-600 font-medium">
                {t('common.serviceUnavailable')}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative mx-auto w-fit">
              <button
                onClick={() => handlePlanSelect('standard')}
                className={`relative ${config.buttonStyle} ${config.buttonTextColor} ${config.buttonBorderRadius} px-8 py-4 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-between group mx-auto border-none`}
                disabled={isLoading}
                aria-label={`Start your ${serviceType} subscription`}
              >
              {/* Button content */}
              <div className="relative z-10 flex items-center justify-center w-full">
                {isLoading ? (
                  <span className="flex items-center">
                    <div className={`w-5 h-5 border-t-2 border-r-2 ${
                      config.buttonTextColor === 'text-white' ? 'border-white' : 'border-gray-900'
                    } rounded-full animate-spin mr-2`}></div>
                    {t('common.loading')}
                  </span>
                ) : (
                  <span className="flex items-center">
                    {t(`${config.namespace}.hero.pricingButton`, { 
                      currency: t('common.currency'),
                      amount: config.pricing.amount,
                      count: config.pricing.deliveries,
                      period: t(`common.${config.pricing.period}`)
                    })}
                    <CircleArrowRight className="ml-2 w-6 h-6 text-white group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </div>
              
                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-200"></div>
              </button>
              
              {/* Promotional badge */}
              {promoInfo && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg z-20">
                  {promoInfo}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <p className="text-gray-500 text-sm">{t(`${config.namespace}.hero.noCommitment`)}</p>
            </div>
          </div>
        )}
        
        {/* Hero image */}
        <div className="mt-8 relative">
          <div className="w-full aspect-square rounded-2xl overflow-hidden relative">
            <div 
              className={`w-full h-full ${config.imageScale}`}
              style={config.customObjectPosition ? { objectPosition: config.customObjectPosition } : undefined}
            >
              <OptimizedImageByName 
                imageName={config.heroImageName} 
                alt={`Hero image showcasing our ${serviceType} subscription service`}
                className={`w-full h-full object-cover ${config.imagePosition}`}
                loading="eager"
                onError={() => {
                  console.error('Failed to load hero image');
                }}
              />
            </div>
            
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
}; 